import type { PermissionsMap } from "@printflow/auth-shared";
import type { OrdenDeImpresora } from "../paginas/impresoras/impresora";
import type { PrecioResuelto } from "../paginas/presupuestos/renglones";
import { ApiError, apiBlob, apiGet, apiMutar, apiSubir } from "./api";

/**
 * Cliente de los endpoints de la consola.
 *
 * Un archivo por contrato, no uno por pantalla: el listado de usuarios y el
 * detalle consumen lo mismo, y tenerlo dos veces es como termina divergiendo.
 */

// ─── Roles y permisos ──────────────────────────────────────────────────────

export interface Permiso {
  codigo: string;
  recurso: string;
  accion: string;
  tipo: "CREATE" | "READ" | "UPDATE" | "DELETE" | "SPECIAL";
  descripcion: string;
}

export interface Rol {
  nombre: string;
  tipo: "base" | "dinamico";
  /** Los base no se editan ni se borran: sus permisos son codigo (ADR 0014). */
  editable: boolean;
  descripcion: string | null;
  permisos: PermissionsMap;
  miembros: number;
}

export const fetchRoles = () => apiGet<{ roles: Rol[] }>("/api/v1/roles").then((r) => r.roles);

export const fetchCatalogoPermisos = () =>
  apiGet<{ permisos: Permiso[] }>("/api/v1/roles/permisos").then((r) => r.permisos);

export const crearRol = (datos: {
  nombre: string;
  descripcion: string;
  permisos: PermissionsMap;
}) => apiMutar<{ rol: unknown }>("POST", "/api/v1/roles", datos);

export const actualizarRol = (
  nombre: string,
  datos: { descripcion: string; permisos: PermissionsMap },
) => apiMutar<{ rol: unknown }>("PATCH", `/api/v1/roles/${encodeURIComponent(nombre)}`, datos);

export const borrarRol = (nombre: string) =>
  apiMutar<{ ok: true }>("DELETE", `/api/v1/roles/${encodeURIComponent(nombre)}`);

// ─── Usuarios ──────────────────────────────────────────────────────────────

export interface Usuario {
  id: string;
  memberId: string;
  email: string;
  nombre: string;
  telefono: string | null;
  direccion: string | null;
  activo: boolean;
  ultimoLogin: string | null;
  roles: string[];
  permisos: PermissionsMap;
}

export const fetchUsuarios = () =>
  apiGet<{ usuarios: Usuario[] }>("/api/v1/usuarios").then((r) => r.usuarios);

export const fetchUsuario = (id: string) =>
  apiGet<{
    usuario: Usuario;
    /**
     * Si QUIEN pide puede cambiarle datos, roles y estado.
     *
     * Lo decide el servidor: la contención se compara con los permisos
     * COMPLETOS —los internos del plugin incluidos— y esos nunca salen al
     * dashboard (ADR 0018), así que calcularlo acá daría otro resultado.
     */
    gestionable: boolean;
  }>(`/api/v1/usuarios/${id}`);

export const fetchRolesAsignables = () =>
  apiGet<{ roles: string[] }>("/api/v1/usuarios/roles-asignables").then((r) => r.roles);

export const actualizarUsuario = (
  id: string,
  datos: { nombre?: string; telefono?: string | null; direccion?: string | null },
) => apiMutar<{ ok: true }>("PATCH", `/api/v1/usuarios/${id}`, datos);

export const cambiarEstadoUsuario = (id: string, activo: boolean) =>
  apiMutar<{ ok: true }>("PATCH", `/api/v1/usuarios/${id}/estado`, { activo });

export const asignarRoles = (id: string, roles: string[]) =>
  apiMutar<{ ok: true; roles: string[] }>("PUT", `/api/v1/usuarios/${id}/roles`, { roles });

// ─── Invitaciones y estado de envio ────────────────────────────────────────

export interface Invitacion {
  id: string;
  email: string;
  rol: string | null;
  estado: "pending" | "accepted" | "rejected" | "canceled" | "expirada";
  expiraEn: string;
  creadaEn: string;
  invitadaPor: string | null;
}

/** Estado del envio del mail (ADR 0005). `terminal` dice si hay que seguir esperando. */
export interface EstadoEnvio {
  id: string;
  estado: "pendiente" | "enviado" | "fallido";
  intentos: number;
  ultimoError: string | null;
  terminal?: boolean;
}

export const fetchInvitaciones = () =>
  apiGet<{ invitaciones: Invitacion[] }>("/api/v1/invitaciones").then((r) => r.invitaciones);

export const invitar = (datos: { email: string; rol: string }) =>
  apiMutar<{ invitacion: unknown; envio: EstadoEnvio | null }>(
    "POST",
    "/api/v1/invitaciones",
    datos,
  );

export const reenviarInvitacion = (id: string) =>
  apiMutar<{ invitacion: unknown; envio: EstadoEnvio | null }>(
    "POST",
    `/api/v1/invitaciones/${id}/reenviar`,
  );

export const cancelarInvitacion = (id: string) =>
  apiMutar<{ ok: true }>("DELETE", `/api/v1/invitaciones/${id}`);

export const fetchEstadoEnvio = (id: string) => apiGet<EstadoEnvio>(`/api/v1/outbox/${id}`);

// ─── Auditoria ─────────────────────────────────────────────────────────────

export interface EventoAuditoria {
  id: string;
  tipoEvento: string;
  tipoEntidad: string;
  entidadId: string;
  usuarioId: string | null;
  usuarioNombre: string | null;
  usuarioEmail: string | null;
  payload: unknown;
  ipAddress: string | null;
  creadoEn: string;
}

export interface FiltroAuditoria {
  tipoEntidad?: string | undefined;
  tipoEvento?: string | undefined;
  entidadId?: string | undefined;
  desde?: string | undefined;
  hasta?: string | undefined;
  limite?: number | undefined;
  offset?: number | undefined;
}

export const fetchAuditoria = (filtro: FiltroAuditoria) => {
  const params = new URLSearchParams();
  for (const [clave, valor] of Object.entries(filtro)) {
    if (valor !== undefined && valor !== "") params.set(clave, String(valor));
  }
  return apiGet<{ eventos: EventoAuditoria[]; limite: number; offset: number; total: number }>(
    `/api/v1/auditoria?${params}`,
  );
};

export const fetchCatalogoAuditoria = () =>
  apiGet<{ tiposEvento: string[]; tiposEntidad: string[] }>("/api/v1/auditoria/catalogo");

// ─── Catalogos ─────────────────────────────────────────────────────────────

export interface CatalogoItem {
  id: string;
  nombre: string;
  activo: boolean;
  orden?: number;
  [campo: string]: unknown;
}

interface CampoCatalogo {
  clave: string;
  etiqueta: string;
  tipo: "texto" | "numero" | "booleano";
}

export interface DefinicionCatalogo {
  ruta: string;
  titulo: string;
  ordenable: boolean;
  campos: CampoCatalogo[];
  /** Que mostrar en la columna "Detalle" del listado. */
  detalle: (item: CatalogoItem) => string;
}

/**
 * Los cinco catalogos, con lo que la UI necesita para cada uno.
 *
 * El backend ya conoce sus campos; esto agrega lo que es puramente de pantalla
 * —etiquetas en castellano, tipo de input, que resumir en el listado— y por eso
 * vive aca y no en `auth-shared`.
 */
/** Un estado de stock, tal como lo devuelve el worker. */
export interface EstadoStock {
  value: string;
  label: string;
  descripcion: string;
}

/**
 * Los estados de stock: **solo lectura**, son los valores del enum.
 *
 * `imprenta` los muestra como una pestaña más de Parámetros, con su etiqueta y
 * su descripción (ADR 0071).
 */
export const fetchEstadosStock = () =>
  apiGet<{ items: EstadoStock[] }>("/api/v1/catalogos/estados-stock").then((r) => r.items);

export const CATALOGOS: readonly DefinicionCatalogo[] = [
  {
    ruta: "tipos-trabajo",
    titulo: "Tipos de trabajo",
    ordenable: false,
    campos: [{ clave: "descripcion", etiqueta: "Descripcion", tipo: "texto" }],
    detalle: (i) => (i.descripcion as string) ?? "—",
  },
  {
    ruta: "tipos-impresora",
    titulo: "Tipos de impresora",
    ordenable: false,
    campos: [{ clave: "descripcion", etiqueta: "Descripcion", tipo: "texto" }],
    detalle: (i) => (i.descripcion as string) ?? "—",
  },
  {
    ruta: "tipos-tamano-logo",
    titulo: "Tamaños de logo",
    ordenable: true,
    campos: [],
    // Las dimensiones en mm son por producto, no del catalogo (ADR 0010).
    detalle: () => "Las medidas se definen por producto",
  },
  {
    ruta: "tipos-base",
    titulo: "Tipos de base",
    ordenable: true,
    campos: [],
    detalle: () => "—",
  },
  {
    ruta: "formas-pago",
    titulo: "Formas de pago",
    ordenable: false,
    campos: [
      { clave: "descripcion", etiqueta: "Descripcion", tipo: "texto" },
      { clave: "diasCredito", etiqueta: "Dias de credito", tipo: "numero" },
      { clave: "requiereFactura", etiqueta: "Requiere factura", tipo: "booleano" },
      { clave: "descuentoProntoPago", etiqueta: "Descuento pronto pago (%)", tipo: "numero" },
      { clave: "diasDescuentoProntoPago", etiqueta: "Dias para el descuento", tipo: "numero" },
    ],
    detalle: (i) => {
      const dias = Number(i.diasCredito ?? 0);
      const base = dias > 0 ? `${dias} dias de credito` : "Contado";
      return i.requiereFactura ? `${base} · requiere factura` : base;
    },
  },
];

export const fetchCatalogo = (ruta: string, incluirInactivos = false) =>
  apiGet<{ items: CatalogoItem[] }>(
    `/api/v1/catalogos/${ruta}${incluirInactivos ? "?incluirInactivos=1" : ""}`,
  ).then((r) => r.items);

export const crearItemCatalogo = (ruta: string, datos: Record<string, unknown>) =>
  apiMutar<{ item: CatalogoItem }>("POST", `/api/v1/catalogos/${ruta}`, datos);

export const actualizarItemCatalogo = (ruta: string, id: string, datos: Record<string, unknown>) =>
  apiMutar<{ item: CatalogoItem }>("PATCH", `/api/v1/catalogos/${ruta}/${id}`, datos);

export const borrarItemCatalogo = (ruta: string, id: string) =>
  apiMutar<{ ok: true }>("DELETE", `/api/v1/catalogos/${ruta}/${id}`);

export const reordenarCatalogo = (ruta: string, items: { id: string; orden: number }[]) =>
  apiMutar<{ items: CatalogoItem[] }>("PUT", `/api/v1/catalogos/${ruta}/orden`, { items });

// ─── Clientes e importacion (ADR 0016) ─────────────────────────────────────

export interface Cliente {
  id: string;
  razonSocial: string;
  nombreFantasia: string;
  tipoCliente: string;
  cuit: string | null;
  direccion: string | null;
  observaciones: string | null;
  esVip: boolean;
  creadoEn: string;
  /**
   * Los del cliente, para armar la columna «Contacto» del listado. El detalle
   * los devuelve aparte y con su `id`; acá alcanza con lo que se muestra.
   */
  contactos: {
    nombre: string | null;
    email: string | null;
    telefono: string | null;
    esPredeterminado: boolean;
  }[];
}

export const fetchClientes = (busqueda = "") =>
  apiGet<{ clientes: Cliente[] }>(
    `/api/v1/clientes${busqueda ? `?q=${encodeURIComponent(busqueda)}` : ""}`,
  ).then((r) => r.clientes);

export interface ContactoCliente {
  id: string;
  nombre: string | null;
  email: string | null;
  telefono: string | null;
  observacion: string | null;
  /** A quien se le manda el presupuesto. Siempre hay exactamente uno. */
  esPredeterminado: boolean;
}

export const fetchCliente = (id: string) =>
  apiGet<{ cliente: Cliente; contactos: ContactoCliente[] }>(`/api/v1/clientes/${id}`);

export const crearCliente = (datos: Record<string, unknown>) =>
  apiMutar<{ cliente: Cliente }>("POST", "/api/v1/clientes", datos);

export const actualizarCliente = (id: string, datos: Record<string, unknown>) =>
  apiMutar<{ cliente: Cliente }>("PATCH", `/api/v1/clientes/${id}`, datos);

export const borrarCliente = (id: string) =>
  apiMutar<{ ok: true }>("DELETE", `/api/v1/clientes/${id}`);

export interface CampoImportacion {
  clave: string;
  nombreAmigable: string;
  obligatorio: boolean;
  descripcion: string;
}

export type FilaImportacion = Record<string, unknown>;

export interface ResultadoImportacion {
  simulado?: boolean;
  total: number;
  importables: number;
  conError: number;
  importados?: number;
  filas?: { fila: number; errores: string[] }[];
}

export const fetchCamposImportacion = () =>
  apiGet<{ campos: CampoImportacion[] }>("/api/v1/clientes/importar/campos").then((r) => r.campos);

/**
 * Manda las filas ya parseadas en el browser.
 *
 * Con `simular` valida sin escribir: es lo que usa la previsualizacion, para que
 * lo que el usuario ve salga del MISMO codigo que despues importa (ADR 0016).
 */
export const importarClientes = (filas: FilaImportacion[], simular: boolean) =>
  apiMutar<ResultadoImportacion>(
    "POST",
    `/api/v1/clientes/importar${simular ? "?simular=1" : ""}`,
    { filas },
  );

// ─── Importacion de productos y precios ────────────────────────────────────

export interface SimulacionProductos {
  simulado?: boolean;
  total: number;
  importables: number;
  conError: number;
  importados?: number;
  vigenteDesde?: string;
  vigenteHasta?: string | null;
  /** El dia en que se cierran los precios que esta importacion reemplaza. */
  fechaCierre?: string;
  aCrear?: {
    productos: string[];
    tamanosDeCatalogo: string[];
    basesDeCatalogo: string[];
    precios: number;
  };
  preciosCerrados?: number;
  filas?: { fila: number; errores: string[] }[];
}

export const fetchCamposImportacionProductos = () =>
  apiGet<{ campos: CampoImportacion[] }>("/api/v1/productos/importar/campos").then((r) => r.campos);

/**
 * Importa la planilla de productos y precios. `simular` valida sin escribir: es
 * el MISMO code path, asi que la previsualizacion no puede mentir.
 */
export const importarProductos = (
  filas: FilaImportacion[],
  simular: boolean,
  vigencia: { vigenteDesde?: string; vigenteHasta?: string | null } = {},
) =>
  apiMutar<SimulacionProductos>(
    "POST",
    `/api/v1/productos/importar${simular ? "?simular=1" : ""}`,
    {
      filas,
      ...vigencia,
    },
  );

// ─── Productos y sus combinaciones (ADR 0010) ──────────────────────────────

export interface Producto {
  id: string;
  nombre: string;
  activo: boolean;
  tamanos: number;
  bases: number;
  /** Precios VIGENTES hoy: responde si el producto se puede cotizar ahora. */
  precios: number;
}

export interface TamanoDeProducto {
  id: string;
  tipoTamanoLogoId: string;
  nombre: string;
  anchoMm: string | null;
  altoMm: string | null;
}

export interface BaseDeProducto {
  id: string;
  tipoBaseId: string;
  nombre: string;
}

export const fetchProductos = (incluirInactivos = false) =>
  apiGet<{ productos: Producto[] }>(
    `/api/v1/productos${incluirInactivos ? "?incluirInactivos=1" : ""}`,
  ).then((r) => r.productos);

export const fetchProducto = (id: string) =>
  apiGet<{
    producto: { id: string; nombre: string; activo: boolean };
    tamanos: TamanoDeProducto[];
    bases: BaseDeProducto[];
    /** Los VIGENTES hoy — el rotulo de la pestaña Precios (ADR 0065). */
    precios: number;
  }>(`/api/v1/productos/${id}`);

export const crearProducto = (nombre: string) =>
  apiMutar<{ producto: Producto }>("POST", "/api/v1/productos", { nombre });

export const actualizarProducto = (id: string, datos: Record<string, unknown>) =>
  apiMutar<{ producto: Producto }>("PATCH", `/api/v1/productos/${id}`, datos);

export const borrarProducto = (id: string) =>
  apiMutar<{ ok: true }>("DELETE", `/api/v1/productos/${id}`);

export const agregarTamano = (
  productoId: string,
  datos: { tipoTamanoLogoId: string; anchoMm: string | null; altoMm: string | null },
) =>
  apiMutar<{ tamano: TamanoDeProducto }>("POST", `/api/v1/productos/${productoId}/tamanos`, datos);

/**
 * Corrige las medidas de un tamaño ya asignado.
 *
 * Existe para no obligar a quitar y volver a agregar: quitar borraria los
 * precios que cuelgan de ese tamaño, y corregir un ancho no deberia costar la
 * tarifa.
 */
export const actualizarTamano = (
  productoId: string,
  tamanoId: string,
  datos: { anchoMm: string | null; altoMm: string | null },
) =>
  apiMutar<{ tamano: TamanoDeProducto }>(
    "PATCH",
    `/api/v1/productos/${productoId}/tamanos/${tamanoId}`,
    datos,
  );

export const quitarTamano = (productoId: string, tamanoId: string) =>
  apiMutar<{ ok: true }>("DELETE", `/api/v1/productos/${productoId}/tamanos/${tamanoId}`);

export const agregarBase = (productoId: string, tipoBaseId: string) =>
  apiMutar<{ base: BaseDeProducto }>("POST", `/api/v1/productos/${productoId}/bases`, {
    tipoBaseId,
  });

export const quitarBase = (productoId: string, baseId: string) =>
  apiMutar<{ ok: true }>("DELETE", `/api/v1/productos/${productoId}/bases/${baseId}`);

// ─── Lista de Precios (ADR 0010, 0012, 0023) ───────────────────────────────

/**
 * Una combinación tamaño × base que **no tiene ningún precio vigente hoy**.
 *
 * No es una fila del listado: es una fila que FALTA. Por eso viaja aparte de
 * `precios` — no se puede filtrar la tabla a algo que no está en ella.
 */
export interface CombinacionSinPrecio {
  productoId: string;
  producto: string;
  tamanoId: string;
  tipoTamanoId: string;
  tamano: string;
  baseId: string;
  tipoBaseId: string;
  base: string;
  /** Hasta cuándo se pudo cotizar. `null` si nunca tuvo precio. */
  ultimoVencimiento: string | null;
}

export interface ResumenPrecios {
  total: number;
  vigentes: number;
  futuros: number;
  expirados: number;
  /** Combinaciones sin precio: no se puede cotizar. */
  sinPrecioVigente: number;
  /** Precios que empiezan a regir dentro de los próximos 30 días. */
  cambianEnMes: number;
  /** Vigentes que vencen en 30 días y no dejan sucesor. */
  vencenSinReemplazo: number;
  ultimoAumento: { fecha: string; porcentaje: string; precios: number } | null;
}

export interface PrecioListado {
  id: string;
  producto: string;
  tamanoId: string;
  tamano: string;
  /** El id del TIPO de tamaño — uno por catálogo, no uno por producto. */
  tipoTamanoId: string;
  baseId: string;
  base: string;
  tipoBaseId: string;
  productoActivo: boolean;
  cantidadDesde: number;
  cantidadHasta: number | null;
  precio: string;
  precioVip: string | null;
  esImporteFijo: boolean;
  vigenteDesde: string;
  vigenteHasta: string | null;
  estado: "vigente" | "futuro" | "expirado";
  /** Empieza a regir dentro de los próximos 30 días. */
  cambiaEnMes: boolean;
  /** Vence en 30 días y no hay otro precio que lo reemplace. */
  venceSinReemplazo: boolean;
}

export interface SimulacionAumento {
  afectados: number;
  aCerrar: number;
  fechaCierre: string;
  muestra: {
    precioId: string;
    antes: string;
    despues: string;
    vipAntes: string | null;
    vipDespues: string | null;
  }[];
}

/**
 * Listado de precios, que **es tambien el historial**: trae expirados y futuros
 * con su `estado`, porque la vigencia temporal es el modelo (ADR 0012). Con
 * `tamanoId` y `baseId` se acota a UNA combinacion, que es el historial que
 * `imprenta` sirve por un endpoint aparte.
 */
export const fetchPrecios = (
  productoId = "",
  estado = "",
  combinacion: { tamanoId?: string; baseId?: string; incluirInactivos?: boolean } = {},
) => {
  const params = new URLSearchParams();
  if (productoId) params.set("productoId", productoId);
  if (estado) params.set("estado", estado);
  if (combinacion.tamanoId) params.set("tamanoId", combinacion.tamanoId);
  if (combinacion.baseId) params.set("baseId", combinacion.baseId);
  if (combinacion.incluirInactivos) params.set("incluirInactivos", "1");
  return apiGet<{
    precios: PrecioListado[];
    combinacionesSinPrecio: CombinacionSinPrecio[];
    resumen: ResumenPrecios;
  }>(`/api/v1/precios?${params}`);
};

/**
 * Resuelve la tarifa de una combinacion, cantidad y cliente — la misma que el
 * worker va a congelar al guardar el renglon (ADR 0012).
 *
 * El 404 es una respuesta legitima («no hay tarifa vigente»), no una falla: se
 * traduce a `null` para que la pantalla lo distinga de un error de red. Todo lo
 * demas se propaga, incluido el `AbortError` de una consulta cancelada.
 */
export const resolverTarifa = (
  consulta: { tamanoId: string; baseId: string; cantidad: number; clienteId: string | null },
  signal?: AbortSignal,
): Promise<PrecioResuelto | null> => {
  const params = new URLSearchParams({
    tamanoId: consulta.tamanoId,
    baseId: consulta.baseId,
    cantidad: String(consulta.cantidad),
  });
  if (consulta.clienteId) params.set("clienteId", consulta.clienteId);
  return apiGet<{ precio: PrecioResuelto }>(
    `/api/v1/precios/resolver?${params}`,
    signal ? { signal } : undefined,
  )
    .then((r) => r.precio)
    .catch((err: unknown) => {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    });
};

export const crearPrecio = (datos: Record<string, unknown>) =>
  apiMutar<{ precio: PrecioListado }>("POST", "/api/v1/precios", datos);

/** Cerrar la vigencia es la unica edicion habilitada: el precio no se reescribe. */
export const cerrarVigencia = (id: string, vigenteHasta: string) =>
  apiMutar<{ precio: PrecioListado }>("PATCH", `/api/v1/precios/${id}`, { vigenteHasta });

/**
 * Borra un precio. La pantalla solo lo ofrece sobre los **programados**: uno
 * vigente o expirado es lo que explica un presupuesto ya emitido.
 */
export const borrarPrecio = (id: string) =>
  apiMutar<{ ok: true }>("DELETE", `/api/v1/precios/${id}`);

export const aplicarAumento = (datos: Record<string, unknown>, simular: boolean) =>
  apiMutar<SimulacionAumento & { creados?: number }>(
    "POST",
    `/api/v1/precios/aumento${simular ? "?simular=1" : ""}`,
    datos,
  );

// ─── Presupuestos y produccion (S5/S6) ─────────────────────────────────────

export interface PresupuestoListado {
  id: string;
  numero: string | null;
  estado: string;
  montoTotal: string | null;
  cliente: string;
  clienteId: string;
  creadoEn: string;
  /** Cuantos renglones tiene. Es una columna de la tabla de `imprenta`. */
  items: number;
  /** Quien lo cargo: decide si esta persona puede editarlo (ADR 0028). */
  creadoPorId: string;
  validoHasta: string | null;
  /** Columna de la tabla de `imprenta`. `null` si no se eligió ninguna. */
  formaPago: string | null;
}

export interface RenglonPresupuesto {
  id: string;
  productoId: string | null;
  productoNombre: string | null;
  productoTamanoLogoId: string | null;
  productoTipoBaseId: string | null;
  cantidad: number;
  precioUnitario: string | null;
  /** `null` cuando el precio se cargo a mano: no sale del catalogo (ADR 0012). */
  precioSnapshot: unknown;
  /** Resueltos del catalogo por el endpoint de detalle; ausentes en el listado. */
  producto?: string | null;
  tamano?: string | null;
  base?: string | null;
}

export const fetchPresupuestos = (estado = "") =>
  apiGet<{ presupuestos: PresupuestoListado[] }>(
    `/api/v1/presupuestos${estado ? `?estado=${estado}` : ""}`,
  ).then((r) => r.presupuestos);

/** Un contacto del cliente, como lo lista `BudgetDetail.tsx` de `imprenta`. */
export interface ContactoCliente {
  id: string;
  nombre: string | null;
  email: string | null;
  telefono: string | null;
  observacion: string | null;
}

/**
 * Los nombres detrás de las claves foráneas de la consulta.
 *
 * Aparte de `presupuesto` —que es la fila cruda— porque son derivados. La
 * pantalla `/presupuestos/:id` se abre por URL directa y no tiene el listado
 * detrás que antes le prestaba el nombre del cliente (ADR 0064).
 */
export interface ConsultaRelacionados {
  cliente: string;
  clienteEsVip: boolean;
  formaPago: string | null;
  creadoPor: string;
  canceladoPor: string | null;
  contactos: ContactoCliente[];
}

/** Una OT nacida de esta consulta, para la tabla del pie. */
export interface OrdenDeLaConsulta {
  id: string;
  numero: string;
  producto: string;
  estado: string;
  presupuestoItemId: string | null;
}

export const fetchPresupuesto = (id: string) =>
  apiGet<{
    presupuesto: PresupuestoListado & {
      observacionCancelacion: string | null;
      fechaCancelacion: string | null;
      formaPagoId: string | null;
    };
    items: RenglonPresupuesto[];
    relacionados: ConsultaRelacionados;
    producciones: OrdenDeLaConsulta[];
    /** Renglones que todavía no tienen OT: decide si convertir tiene algo que hacer. */
    itemsPendientes: number;
    editable: boolean;
  }>(`/api/v1/presupuestos/${id}`);

export const crearPresupuesto = (datos: Record<string, unknown>) =>
  apiMutar<{ presupuesto: PresupuestoListado }>("POST", "/api/v1/presupuestos", datos);

/**
 * Edita un presupuesto (ADR 0028). Los renglones se **reemplazan enteros**: lo
 * que se manda es el estado final, no una diferencia.
 */
export const actualizarPresupuesto = (id: string, datos: Record<string, unknown>) =>
  apiMutar<{ presupuesto: PresupuestoListado }>("PATCH", `/api/v1/presupuestos/${id}`, datos);

export const cambiarEstadoPresupuesto = (id: string, estado: string, observacion?: string) =>
  apiMutar<{ presupuesto: PresupuestoListado }>("POST", `/api/v1/presupuestos/${id}/estado`, {
    estado,
    ...(observacion ? { observacion } : {}),
  });

export const convertirPresupuesto = (id: string) =>
  apiMutar<{ creadas: number; numeros: string[] }>("POST", `/api/v1/presupuestos/${id}/convertir`);

export interface OrdenListada {
  id: string;
  numero: string;
  estado: string;
  prioridad: string;
  producto: string;
  cantidad: number;
  cliente: string;
  responsable: string | null;
  /** El worker ya lo devolvía; lo necesita el select de responsable en la fila. */
  responsableId: string | null;
  impresora: string | null;
  /** El worker ya lo devolvía; lo necesita el select de impresora en la fila. */
  impresoraId: string | null;
  /** `null` sin `produccion.precios` — el Operario no ve importes. */
  total: string | null;
  /** La consulta de la que salió. `null` en una OT creada a mano (ADR 0053). */
  presupuestoId: string | null;
  presupuestoNumero: string | null;
  /**
   * `YYYY-MM-DD`. El worker ya la devolvía; el tipo no la declaraba.
   * Es la fecha por la que agrupa el calendario de entregas (ADR 0047).
   */
  fechaPactadaEntrega: string | null;
}

export const fetchOrdenes = (estado = "") =>
  apiGet<{ ordenes: OrdenListada[] }>(
    `/api/v1/produccion${estado ? `?estado=${estado}` : ""}`,
  ).then((r) => r.ordenes);

/** El calendario de entregas conserva las canceladas (ADR 0100 C2); el tablero no. */
export const fetchOrdenesCalendario = () =>
  apiGet<{ ordenes: OrdenListada[] }>("/api/v1/produccion?canceladas=1").then((r) => r.ordenes);

/**
 * Los nombres detrás de las claves foráneas de la OT.
 *
 * Van aparte de `orden` —que es la fila cruda— porque son derivados. `null`
 * solo si la OT desapareció entre las dos consultas del endpoint.
 */
export interface OrdenRelacionados {
  cliente: string;
  tipoTrabajo: string | null;
  impresora: string | null;
  responsable: string | null;
  presupuestoNumero: string | null;
  creadoPor: string;
}

export const fetchOrden = (id: string) =>
  apiGet<{
    orden: Record<string, unknown> & { numero: string; estado: string };
    relacionados: OrdenRelacionados | null;
    artes: number;
    transicionesDisponibles: string[];
    historial: {
      id: string;
      estadoAnterior: string;
      estadoNuevo: string;
      /** Nombre de quien hizo el cambio; `null` si el usuario ya no existe. */
      usuario: string | null;
      ocurrioEn: string;
      motivo: string | null;
      /** Salto forzado (ADR 0098 B9): se muestra distinto de un paso normal. */
      forzada: boolean;
    }[];
    pausas: { id: string; inicioPausa: string; finPausa: string | null }[];
  }>(`/api/v1/produccion/${id}`);

/**
 * Cambia el estado de una OT. `forzar: true` es el salto forzado de ADR 0098
 * (B1): exige motivo y uno de los dos permisos de salto, y el worker nunca lo
 * degrada al paso ordinario. Se manda solo cuando es `true`.
 */
export const cambiarEstadoOrden = (
  id: string,
  estado: string,
  opciones: { motivo?: string; forzar?: boolean } = {},
) =>
  apiMutar<{ ok: true; estado: string }>("POST", `/api/v1/produccion/${id}/estado`, {
    estado,
    ...(opciones.motivo ? { motivo: opciones.motivo } : {}),
    ...(opciones.forzar === true ? { forzar: true } : {}),
  });

/**
 * Elimina una OT en `BORRADOR`: la pasa a `ELIMINADA`, que no se restaura
 * (ADR 0100 B3). El motivo es opcional (B4) y se manda solo si viene.
 */
export const eliminarOrden = (id: string, motivo?: string) =>
  apiMutar<void>("DELETE", `/api/v1/produccion/${id}`, motivo ? { motivo } : undefined);

/** Alta de una OT sin presupuesto (ADR 0053). Devuelve su numero recien generado. */
export const crearOrden = (datos: Record<string, unknown>) =>
  apiMutar<{ id: string; numero: string }>("POST", "/api/v1/produccion", datos);

export const asignarOrden = (
  id: string,
  datos: { impresoraId?: string | null; responsableId?: string | null },
) => apiMutar<{ ok: true }>("PATCH", `/api/v1/produccion/${id}/asignacion`, datos);

export const registrarHoras = (
  id: string,
  datos: { fecha: string; horas: number; comentario?: string },
) => apiMutar<{ ok: true; hsReales: string }>("POST", `/api/v1/produccion/${id}/horas`, datos);

// ─── Impresoras ────────────────────────────────────────────────────────────

export type EstadoImpresora = "IDLE" | "BUSY" | "OFFLINE" | "MAINTENANCE";

export interface Impresora {
  id: string;
  nombre: string;
  modelo: string | null;
  estado: EstadoImpresora;
  activa: boolean;
  observaciones: string | null;
  tipoImpresoraId: string | null;
  tipo: string | null;
  /** OTs vivas asignadas. Viene del listado, agregado en una sola query. */
  ordenesActivas: number;
}

/**
 * `imprenta` tiene tres endpoints —`/`, `/workload`, `/available`— y acá es uno
 * con filtros. `incluirInactivas` trae también las dadas de baja.
 */
export const fetchImpresoras = (opciones: { estado?: string; incluirInactivas?: boolean } = {}) => {
  const params = new URLSearchParams();
  if (opciones.estado) params.set("estado", opciones.estado);
  if (opciones.incluirInactivas) params.set("activas", "false");
  const query = params.toString();
  return apiGet<{ impresoras: Impresora[] }>(`/api/v1/impresoras${query ? `?${query}` : ""}`).then(
    (r) => r.impresoras,
  );
};

export const fetchImpresora = (id: string) =>
  apiGet<{
    impresora: Impresora & { creadoEn: string; actualizadoEn: string };
    /** TODAS las vivas, no solo las activas: la ficha las reparte en tres bloques. */
    ordenes: OrdenDeImpresora[];
  }>(`/api/v1/impresoras/${id}`);

export interface CuerpoImpresora {
  nombre?: string;
  modelo?: string | null;
  tipoImpresoraId?: string | null;
  observaciones?: string | null;
  estado?: EstadoImpresora;
  activa?: boolean;
}

export const crearImpresora = (datos: CuerpoImpresora) =>
  apiMutar<{ impresora: Impresora }>("POST", "/api/v1/impresoras", datos);

export const actualizarImpresora = (id: string, datos: CuerpoImpresora) =>
  apiMutar<{ impresora: Impresora }>("PATCH", `/api/v1/impresoras/${id}`, datos);

export const borrarImpresora = (id: string) =>
  apiMutar<{ ok: true }>("DELETE", `/api/v1/impresoras/${id}`);

// ─── Feature flags (ADR 0025) ──────────────────────────────────────────────

export interface FlagConsola {
  clave: string;
  descripcion: string;
  porDefecto: boolean;
  tipo: "endpoint" | "regla";
  habilitado: boolean;
  /** El valor viene de la base y no del default: alguien lo decidio. */
  modificado: boolean;
}

export const fetchFlags = () =>
  apiGet<{ flags: FlagConsola[] }>("/api/v1/flags").then((r) => r.flags);

export const guardarFlag = (clave: string, datos: { habilitado: boolean; motivo?: string }) =>
  apiMutar<{ ok: true }>("PUT", `/api/v1/flags/${encodeURIComponent(clave)}`, datos);

export interface ParteHora {
  id: string;
  fecha: string;
  horas: string;
  comentario: string | null;
  usuarioId: string;
  usuario: string | null;
}

export const fetchPartesHora = (id: string) =>
  apiGet<{ partes: ParteHora[]; totalHoras: string; hsReales: string }>(
    `/api/v1/produccion/${id}/horas`,
  );

export const corregirHoras = (
  id: string,
  parteId: string,
  datos: { fecha?: string; horas?: number; comentario?: string },
) =>
  apiMutar<{ ok: true; hsReales: string }>(
    "PATCH",
    `/api/v1/produccion/${id}/horas/${parteId}`,
    datos,
  );

export const borrarHoras = (id: string, parteId: string) =>
  apiMutar<{ ok: true; hsReales: string }>("DELETE", `/api/v1/produccion/${id}/horas/${parteId}`);

// ─── Cola de facturación (ADR 0026) ────────────────────────────────────────

export interface AlertaFacturacion {
  id: string;
  estado: "pendiente" | "en_proceso" | "facturado" | "no_facturable" | "cancelado";
  observaciones: string | null;
  facturaRef: string | null;
  fechaProcesado: string | null;
  creadoEn: string;
  procesadoPor: string | null;
  /** `null`: la creo el automatismo de ENTREGADA; con nombre, una persona (ADR 0099). */
  creadaPor: string | null;
  motivoNoFacturable: string | null;
  notaCreditoRef: string | null;
  anuladaEn: string | null;
  anuladaPor: string | null;
  documentos: { tipo: "comprobante" | "nota_credito"; nombreArchivo: string; bytes: number }[];
  produccionId: string;
  numero: string;
  estadoOrden: string;
  producto: string;
  cantidad: number;
  total: string | null;
  cliente: string;
}

export const fetchAlertasFacturacion = (estado?: string) =>
  apiGet<{ alertas: AlertaFacturacion[] }>(
    `/api/v1/facturacion${estado ? `?estado=${encodeURIComponent(estado)}` : ""}`,
  ).then((r) => r.alertas);

export const fetchContadorFacturacion = () =>
  apiGet<{ contador: Record<string, number> }>("/api/v1/facturacion/contador").then(
    (r) => r.contador,
  );

export const tomarAlerta = (id: string, datos?: { observaciones?: string }) =>
  apiMutar<{ alerta: unknown }>("PATCH", `/api/v1/facturacion/${id}/en-proceso`, datos ?? {});

/** Numero de factura y comprobante en el mismo pedido, multipart (ADR 0099 A6). */
export const marcarFacturada = (
  id: string,
  datos: { facturaRef: string; comprobante: File; observaciones?: string },
) => {
  const form = new FormData();
  form.set("facturaRef", datos.facturaRef.trim());
  form.set("comprobante", datos.comprobante);
  if (datos.observaciones?.trim()) form.set("observaciones", datos.observaciones.trim());
  return apiSubir<{ alerta: unknown }>(`/api/v1/facturacion/${id}/facturado`, form, "PATCH");
};

/** «No se factura» desde la cola: `pendiente`/`en_proceso` → `no_facturable` (ADR 0099 C4). */
export const marcarNoFacturable = (id: string, motivo: string) =>
  apiMutar<{ alerta: unknown }>("PATCH", `/api/v1/facturacion/${id}/no-facturable`, {
    motivo: motivo.trim(),
  });

const rutaDeOt = (otId: string, accion: string) =>
  `/api/v1/facturacion/ot/${encodeURIComponent(otId)}/${accion}`;

/** «Emitir alerta» sobre una OT entregada o cerrada sin alerta vigente (ADR 0099 A4). */
export const emitirAlertaDeOt = (otId: string, observaciones?: string) =>
  apiMutar<{ alerta: unknown }>(
    "POST",
    rutaDeOt(otId, "emitir"),
    observaciones?.trim() ? { observaciones: observaciones.trim() } : {},
  );

/** «No se factura» sobre una OT, con motivo de 10 a 500 caracteres (ADR 0099 A4). */
export const noSeFacturaOt = (otId: string, motivo: string) =>
  apiMutar<{ alerta: unknown }>("POST", rutaDeOt(otId, "no-se-factura"), {
    motivo: motivo.trim(),
  });

/** «Reabrir»: `no_facturable` → `cancelado`, y el aviso vuelve (ADR 0099 C4). */
export const reabrirFacturacionOt = (otId: string, motivo: string) =>
  apiMutar<{ alerta: unknown }>("POST", rutaDeOt(otId, "reabrir"), { motivo: motivo.trim() });

/**
 * La alerta vigente de una OT tal como la devuelve el worker: la fila con sus
 * documentos (ADR 0099 B3). No es `AlertaFacturacion`: trae ids, no nombres.
 */
export interface AlertaDeOt {
  id: string;
  estado: AlertaFacturacion["estado"];
  numero: string;
  facturaRef: string | null;
  fechaProcesado: string | null;
  motivoNoFacturable: string | null;
  notaCreditoRef: string | null;
  observaciones: string | null;
  documentos: {
    id: string;
    tipo: "comprobante" | "nota_credito";
    nombreArchivo: string;
    bytes: number;
    creadoEn: string;
  }[];
}

/** `null` si la OT no tiene alerta vigente (200 `{ alerta: null }`); el 404 es OT inexistente o no visible. */
export async function fetchAlertaDeOt(otId: string): Promise<AlertaDeOt | null> {
  const { alerta } = await apiGet<{ alerta: AlertaDeOt | null }>(rutaDeOt(otId, "alerta"));
  return alerta;
}

export const descargarDocumentoDeOt = (otId: string, tipo: "comprobante" | "nota_credito") =>
  apiBlob(rutaDeOt(otId, `documentos/${tipo}`));

/** Revertir con la nota de crédito: archivo, número y motivo (ADR 0099 A2, B2). */
export const revertirFacturacionOt = (
  otId: string,
  datos: { notaCredito: File; notaCreditoRef: string; motivo: string },
) => {
  const form = new FormData();
  form.set("notaCredito", datos.notaCredito);
  form.set("notaCreditoRef", datos.notaCreditoRef.trim());
  form.set("motivo", datos.motivo.trim());
  return apiSubir<{ alerta: unknown }>(rutaDeOt(otId, "revertir"), form);
};

// ─── Reportes de negocio (ADR 0017: sobre `producciones`) ──────────────────

export interface CargaImpresoras {
  impresoras: {
    impresoraId: string;
    impresora: string;
    tipoImpresora: string | null;
    estadoImpresora: EstadoImpresora;
    ordenes: number;
    horasEstimadas: number;
    porEstado: Record<string, number>;
  }[];
  totales: { ordenes: number; horasEstimadas: number; impresorasConCarga: number };
}

export interface LeadTimeReporte {
  items: {
    numero: string;
    cliente: string;
    producto: string;
    brutoHoras: number;
    netoHoras: number;
    pausadoHoras: number;
  }[];
  promedios: { brutoHoras: number; netoHoras: number; pausadoHoras: number };
  entregadas: number;
}

export interface EficienciaReporte {
  items: {
    numero: string;
    producto: string;
    responsable: string | null;
    impresora: string | null;
    eficiencia: {
      estimadoHoras: number;
      realHoras: number;
      ratio: number;
      desvioHoras: number;
    } | null;
  }[];
  sinDatos: number;
  promedio: { estimadoHoras: number; realHoras: number; ratio: number; desvioHoras: number } | null;
}

export interface ResumenProduccion {
  porEstado: { estado: string; ordenes: number; valor: number | null }[];
  /** OTs creadas por dia. Es la serie temporal del tab «resumen» de `imprenta`. */
  porDia: { dia: string; ordenes: number }[];
  totales: { ordenes: number; activas: number; valor: number | null };
  /** Los diez clientes con mas OTs en el rango — el «Top Clientes» de `imprenta`. */
  topClientes: { clienteId: string; cliente: string; ordenes: number; valor: number | null }[];
}

export interface ConversionPresupuestos {
  porEstado: { estado: string; cuantos: number; monto: number }[];
  presupuestos: number;
  aprobados: number;
  convertidos: number;
  tasaAprobacion: number;
  tasaConversion: number;
}

const conRango = (base: string, desde?: string, hasta?: string) => {
  const params = new URLSearchParams();
  if (desde) params.set("desde", desde);
  if (hasta) params.set("hasta", hasta);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
};

export const fetchCargaImpresoras = () =>
  apiGet<CargaImpresoras>("/api/v1/reportes/carga-impresoras");

export const fetchLeadTime = (desde?: string, hasta?: string) =>
  apiGet<LeadTimeReporte>(conRango("/api/v1/reportes/lead-time", desde, hasta));

export const fetchEficiencia = (desde?: string, hasta?: string) =>
  apiGet<EficienciaReporte>(conRango("/api/v1/reportes/eficiencia", desde, hasta));

export const fetchResumenProduccion = (desde?: string, hasta?: string) =>
  apiGet<ResumenProduccion>(conRango("/api/v1/reportes/resumen-produccion", desde, hasta));

export const fetchConversion = (desde?: string, hasta?: string) =>
  apiGet<ConversionPresupuestos>(
    conRango("/api/v1/reportes/conversion-presupuestos", desde, hasta),
  );

// ─── Arte y pruebas de impresion (S7, ADR 0004 y 0011) ─────────────────────

export type EntidadArte = "PRESUPUESTO" | "PRODUCCION";

export interface Arte {
  id: string;
  /** Secuencia de subida, NO version del archivo (ADR 0011). */
  orden: number;
  /** Nombre descriptivo: "frente", "espalda". Distinto del nombre del archivo. */
  nombre: string | null;
  nombreArchivo: string;
  mimeType: string;
  tamanoBytes: number;
  hashSha256: string | null;
  creadoEn: string;
  creadoPor: string | null;
}

export interface Prueba {
  id: string;
  /** Aca SI es version: la segunda prueba es otro intento del mismo trabajo. */
  version: number;
  nombreArchivo: string;
  mimeType: string;
  tamanoBytes: number;
  hashSha256: string | null;
  creadoEn: string;
  creadoPor: string | null;
}

export interface FormatosAdjunto {
  extensiones: string[];
  tamanoMaximo: number;
}

export const fetchFormatosArte = () => apiGet<FormatosAdjunto>("/api/v1/artes/formatos");
export const fetchFormatosPrueba = () => apiGet<FormatosAdjunto>("/api/v1/pruebas/formatos");

export const fetchArtes = (entidadTipo: EntidadArte, entidadId: string) =>
  apiGet<{ artes: Arte[] }>(
    `/api/v1/artes?entidadTipo=${entidadTipo}&entidadId=${encodeURIComponent(entidadId)}`,
  ).then((r) => r.artes);

export const subirArte = (
  entidadTipo: EntidadArte,
  entidadId: string,
  archivo: File,
  nombre: string,
) => {
  const form = new FormData();
  form.set("archivo", archivo);
  form.set("entidadTipo", entidadTipo);
  form.set("entidadId", entidadId);
  if (nombre.trim()) form.set("nombre", nombre.trim());
  return apiSubir<{ arte: Arte }>("/api/v1/artes", form);
};

export const renombrarArte = (id: string, nombre: string | null) =>
  apiMutar<{ arte: Arte }>("PATCH", `/api/v1/artes/${id}`, { nombre });

export const borrarArte = (id: string) => apiMutar<{ ok: true }>("DELETE", `/api/v1/artes/${id}`);

export const fetchPruebas = (produccionId: string) =>
  apiGet<{ pruebas: Prueba[] }>(
    `/api/v1/pruebas?produccionId=${encodeURIComponent(produccionId)}`,
  ).then((r) => r.pruebas);

export const subirPrueba = (produccionId: string, archivo: File) => {
  const form = new FormData();
  form.set("archivo", archivo);
  form.set("produccionId", produccionId);
  return apiSubir<{ prueba: Prueba }>("/api/v1/pruebas", form);
};

export const borrarPrueba = (id: string) =>
  apiMutar<{ ok: true }>("DELETE", `/api/v1/pruebas/${id}`);

/** El archivo, como blob. Ver `apiBlob`: la URL directa no sirve para `<img>`. */
export const descargarAdjunto = (tipo: "artes" | "pruebas", id: string) =>
  apiBlob(`/api/v1/${tipo}/${id}/archivo`);

// ─── Centro de notificaciones (S9b, ADR 0030) ──────────────────────────────

export interface AccionPendiente {
  tipo: string;
  /** Id de la COSA pendiente: la OT, la fila de facturacion. */
  id: string;
  titulo: string;
  detalle: string | null;
  desde: string;
  /** Verbo del dominio: "Facturar", no "Ir a facturar". */
  verbo: string;
  destino: string | null;
  /** Por que el boton esta gris. `null` cuando hay destino. */
  motivoSinDestino: string | null;
}

export interface Aviso {
  id: string;
  tipo: string;
  titulo: string;
  cuerpo: string | null;
  entidadTipo: string | null;
  entidadId: string | null;
  leidaEn: string | null;
  creadoEn: string;
}

export interface Campanita {
  acciones: {
    total: number;
    mostradas: AccionPendiente[];
    /** Cuantas quedaron afuera del techo, por tipo. */
    recortados: Record<string, number>;
  };
  avisos: { sinLeer: number; items: Aviso[] };
}

export type PlazoPostergado = "manana" | "semana";

export const fetchCampanita = () => apiGet<Campanita>("/api/v1/notificaciones");

/**
 * Posterga una alerta **para esta persona**. No la resuelve ni la saca de su
 * cola: `/facturacion` sigue mostrando todo (ADR 0030, salvaguarda 2).
 */
export const postergarAlerta = (tipo: string, referenciaId: string, plazo: PlazoPostergado) =>
  apiMutar<{ postergada: unknown }>(
    "POST",
    `/api/v1/notificaciones/alertas/${tipo}/${encodeURIComponent(referenciaId)}/postergar`,
    { plazo },
  );

/** Leer NUNCA resuelve nada: toca `leidaEn` y ninguna otra cosa. */
export const leerAviso = (id: string) =>
  apiMutar<{ notificacion: Aviso }>("POST", `/api/v1/notificaciones/${id}/leer`);

export const leerTodos = () =>
  apiMutar<{ leidas: number }>("POST", "/api/v1/notificaciones/leer-todas");

// ─── Perfil propio (fase 7) ────────────────────────────────────────────────

/**
 * Edita los datos propios. **No pasa por `/usuarios/:id`**: ese exige
 * `usuarios.update`, que es permiso de administrar a OTROS — un Operario no lo
 * tiene y aun asi debe poder corregirse el telefono.
 */
export const actualizarPerfil = (datos: {
  nombre?: string;
  telefono?: string | null;
  direccion?: string | null;
}) =>
  apiMutar<{ perfil: { id: string; nombre: string; email: string } }>(
    "PATCH",
    "/api/v1/perfil",
    datos,
  );

// ─── Estado del sistema (fase 7) ───────────────────────────────────────────

export interface EstadoSistema {
  entorno: "dev" | "stage" | "prod";
  piezas: {
    nombre: string;
    presente: boolean;
    detalle: string;
    /** Que deja de funcionar sin esto. `null` cuando esta presente. */
    sinEsto: string | null;
    /** Pares etiqueta/valor — el «Remitente:» de la tarjeta SMTP de `imprenta`. */
    datos?: { etiqueta: string; valor: string }[];
  }[];
  outbox: { pendientes: number; fallidos: number; enviados: number };
  auditoria: { eventos: number };
  funcionalidades: {
    reglas: { clave: string; habilitada: boolean }[];
    tiposDeAlerta: string[];
  };
}

export const fetchEstadoSistema = () => apiGet<EstadoSistema>("/api/v1/sistema/estado");

// ─── Datos de la imprenta (ADR 0043) ───────────────────────────────────────

export interface DatoDeConfiguracion {
  clave: string;
  etiqueta: string;
  valor: string;
  actualizadoEn: string | null;
}

export const fetchConfiguracion = () =>
  apiGet<{ datos: DatoDeConfiguracion[] }>("/api/v1/configuracion").then((r) => r.datos);

export const guardarConfiguracion = (cambios: Record<string, string>) =>
  apiMutar<{ actualizados: number }>("PATCH", "/api/v1/configuracion", cambios);

// ─── Export de produccion a CSV (ADR 0045) ─────────────────────────────────

/**
 * Baja el CSV de produccion.
 *
 * Usa `apiBlob` y no una navegacion directa a la URL: el endpoint exige la
 * cookie de sesion **y** `X-Requested-With` (ADR 0036), y un `window.open` no
 * manda la cabecera — daria 403. El nombre del archivo lo pone el servidor en
 * `content-disposition`, pero un blob no lo conserva, asi que se repite aca.
 */
export async function descargarProduccionCsv(desde: string, hasta: string): Promise<void> {
  const params = new URLSearchParams();
  if (desde) params.set("desde", desde);
  if (hasta) params.set("hasta", hasta);
  const query = params.toString();

  const blob = await apiBlob(`/api/v1/reportes/exportar/produccion${query ? `?${query}` : ""}`);

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `produccion_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  // Sin esto el blob queda en memoria hasta que se recargue la pagina.
  URL.revokeObjectURL(url);
}

// ─── Pantalla de inicio (ADR 0046) ─────────────────────────────────────────

export interface TarjetaDeInicio {
  clave: string;
  titulo: string;
  valor: number;
  detalle: string | null;
  /** `null` cuando no hay con que comparar — no es lo mismo que 0%. */
  tendencia: { porcentaje: number; mejora: boolean } | null;
}

export interface VentaReciente {
  numero: string;
  cliente: string | null;
  producto: string;
  total: string | null;
  fechaEntregada: string | null;
}

export const fetchInicio = () =>
  apiGet<{
    tarjetas: TarjetaDeInicio[];
    ventasRecientes: VentaReciente[];
    /** Las que salieron EN EL MES — la lista de arriba está limitada a cinco. */
    ventasDelMes: number;
  }>("/api/v1/inicio");
